import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Modal,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { md5 } from 'js-md5';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import { COLORS } from '@constants/colors';
import { NOTIFICATION_TYPES, useNotification } from '@components/Notification';
import { usePreventGameKeydownListening } from '@hooks';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService, {
  IBrandingHotspotApi,
  IUpdateBrandingHotspotData,
} from '@services/BrandingsService';
import FilesService from '@services/FilesService';

import { useEditorMode } from '../EditorModeContext';
import {
  startHotspotPlaceInUnreal,
  startHotspotCameraViewInUnreal,
  endHotspotCameraViewInUnreal,
  startHotspotEditInUnreal,
  endHotspotEditInUnreal,
} from '../hotspotsUnreal';
import { useInspectorStyles } from './styles';
import { deriveAxesInputs, toTuple } from './axesInputs';

interface HotspotInspectorProps {
  hotspotId: string;
}

type AnyHotspot = Record<string, unknown> & {
  brandingHotspotId?: string;
  name?: string;
  type?: string;
  isActive?: boolean;
  isInteractive?: boolean;
  position?: number[];
  rotation?: number[];
  scale?: number | number[];
  data?: { url?: string; webBrowserUrl?: string; location?: string };
};

const VIDEO_URL_RE = /\.(mp4|webm|mov|m4v|ogg)$/;

const SCALE_MIN = 0.01;
const SCALE_MAX = 100;
const SCALE_DEFAULT = 1;

type TransformField = 'position' | 'rotation' | 'scale';
type Axis = 'x' | 'y' | 'z';

/**
 * Reports whether `url` looks like a supported video by its extension.
 *
 * @param url - URL to inspect.
 */
const isVideoUrl = (url: string): boolean =>
  VIDEO_URL_RE.test(url.toLowerCase().split('?')[0]);

/**
 * Reports whether a numeric value is a finite scale factor within the allowed bounds.
 *
 * @param value - Number to validate.
 */
const isValidScale = (value: number): boolean =>
  Number.isFinite(value) && value >= SCALE_MIN && value <= SCALE_MAX;

/**
 * Parses a raw string from a scale input into a valid scale factor.
 *
 * @param raw - Raw text from the input.
 * @returns The parsed number, or `null` when the input is empty or invalid.
 */
const parseScaleInput = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return null;
  }
  const parsed = Number(trimmed);
  if (!isValidScale(parsed)) {
    return null;
  }
  return parsed;
};

/**
 * Reports whether a raw string is a finite number — the validator for
 * Location/Rotation axis inputs, which (unlike Scale) have no bounds.
 *
 * @param raw - Raw text from the input.
 * @returns The parsed number, or `null` when the input is empty or invalid.
 */
const parseAxisNumber = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Parses a raw axis-input string with the validator appropriate to `field`
 * (bounds-checked for Scale, any finite number for Location/Rotation).
 *
 * @param field - Which transform group the input belongs to.
 * @param raw - Raw text from the input.
 */
const parseFieldInput = (field: TransformField, raw: string): number | null =>
  field === 'scale' ? parseScaleInput(raw) : parseAxisNumber(raw);

const ALLOWED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg'];
const ALLOWED_VIDEO_MIME_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const IMAGE_FILENAME_RE = /\.(png|jpe?g)$/;
const VIDEO_FILENAME_RE = /\.(mp4|webm|mov|m4v)$/;
const MODEL_FILENAME_RE = /\.glb$/;
const MODEL_MIME_TYPE = 'model/gltf-binary';

const getFilename = (url: string): string => {
  if (!url) {
    return '';
  }
  try {
    const parsed = new URL(url, window.location.href);
    return decodeURIComponent(parsed.pathname.split('/').pop() || '');
  } catch {
    return decodeURIComponent(url.split('/').pop() || '');
  }
};

const activeRowStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  borderRadius: '10px',
  backgroundColor: 'rgba(255, 255, 255, 0.045)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
};

const toggleHintStyles = {
  fontSize: '11px',
  lineHeight: 1.45,
  color: 'rgba(255, 255, 255, 0.55)',
  margin: '6px 2px 0',
};

const saveButtonStyles = {
  textTransform: 'none',
  backgroundColor: COLORS.MYTAVERSE_BLUE,
  color: COLORS.WHITE,
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 600,
  padding: '9px 14px',
  boxShadow: 'none',
  gap: '6px',
  '&:hover': { backgroundColor: '#3A46C1', boxShadow: 'none' },
};

const uploadModelBannerStyles = {
  display: 'flex',
  gap: '9px',
  padding: '11px 12px',
  borderRadius: '11px',
  backgroundColor: 'rgba(245, 166, 35, 0.14)',
  color: '#f6d49a',
  marginBottom: '8px',
};

const viewCameraButtonStyles = {
  textTransform: 'none',
  color: COLORS.WHITE,
  borderColor: 'rgba(255, 255, 255, 0.18)',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 500,
  padding: '8px 14px',
  gap: '6px',
  '&:hover': {
    borderColor: 'rgba(255, 255, 255, 0.4)',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
  },
};

const viewCameraButtonActiveStyles = {
  textTransform: 'none',
  backgroundColor: COLORS.MYTAVERSE_BLUE,
  color: COLORS.WHITE,
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 600,
  padding: '9px 14px',
  boxShadow: 'none',
  gap: '6px',
  '&:hover': { backgroundColor: '#3A46C1', boxShadow: 'none' },
};

const deleteButtonStyles = {
  textTransform: 'none',
  color: '#ff8585',
  borderColor: 'rgba(255, 255, 255, 0.18)',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 500,
  padding: '8px 14px',
  gap: '6px',
  '&:hover': {
    borderColor: '#FF6B6B',
    backgroundColor: 'rgba(255, 107, 107, 0.06)',
  },
};

/**
 * Renders the inspector for the selected branding hotspot.
 *
 * Every field autosaves through a PATCH and nudges UE with `SPOTS_UPDATED`. The
 * media card adapts to the hotspot variant: a `.glb` upload for 3D objects, an
 * image/video/URL picker for legacy 2D panels. 3D objects additionally expose
 * the world-interaction switch — off makes the object fully static.
 *
 * A placed hotspot gets per-axis Location/Rotation/Scale editors (no
 * uniform-scale mode — every commit sends full `{ position, rotation, scale }`
 * arrays together, since UE currently drops a partial transform update) and
 * runs a `START_HOTSPOT_EDIT`/`END_HOTSPOT_EDIT` gizmo session for as long as
 * it stays selected. It also live-syncs those three editors whenever UE
 * settles an in-world gizmo drag (`FINISH_HOTSPOT_PLACE_2D`/`_3D`, republished
 * via `hotspotLiveTransform`). A placed 3D object additionally gets a
 * "View in world" toggle that points the player's camera at the hotspot via
 * `START_VIEW_HOTSPOT`/`END_VIEW_HOTSPOT` — a separate, purely live UE session,
 * never persisted.
 *
 * @param props.hotspotId - `brandingHotspotId` of the hotspot being edited.
 */
const HotspotInspector = ({ hotspotId }: HotspotInspectorProps) => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const rootRef = usePreventGameKeydownListening<HTMLDivElement>();
  const { setAutosaveStatus, clearSelection } = useEditorMode();
  const {
    brandingHotspots,
    setBrandingHotspots,
    currentBrandingId,
    currentRoom,
    setPendingHotspotPlacement,
    hotspotLiveTransform,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const { showNotification } = useNotification();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Camera-view session state (START_VIEW_HOTSPOT / END_VIEW_HOTSPOT). UE
  // never closes this on its own, so `isViewingCameraRef` lets the cleanup
  // effect below end the session reliably even from a stale closure.
  const [isViewingCamera, setIsViewingCamera] = useState(false);
  const isViewingCameraRef = useRef(false);

  const hotspot = useMemo<AnyHotspot | null>(() => {
    const source = Array.isArray(brandingHotspots) ? brandingHotspots : [];
    return (
      (source as AnyHotspot[]).find((h) => h.brandingHotspotId === hotspotId) ??
      null
    );
  }, [brandingHotspots, hotspotId]);

  const is3D = String(hotspot?.type ?? '').toUpperCase() === 'OBJECT_3D';
  const isPlacedInWorld =
    Array.isArray(hotspot?.position) && (hotspot?.position?.length ?? 0) > 0;
  const fileUrl = hotspot?.data?.url || '';
  const externalUrl = hotspot?.data?.webBrowserUrl || '';
  const effectiveUrl = fileUrl || externalUrl;
  const storedKind: 'IMAGE' | 'VIDEO' = isVideoUrl(effectiveUrl)
    ? 'VIDEO'
    : 'IMAGE';

  const storedPosition = hotspot?.position as number[] | undefined;
  const storedRotation = hotspot?.rotation as number[] | undefined;
  const storedScale = hotspot?.scale as number | number[] | undefined;

  const [name, setName] = useState<string>(hotspot?.name ?? '');
  const [mediaKind, setMediaKind] = useState<'IMAGE' | 'VIDEO'>(storedKind);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // Lazy initializers — `useState` reads the initializer once on mount, so
  // deriving inside a `useMemo` on every render was dead work.
  const [positionXInput, setPositionXInput] = useState<string>(
    () => deriveAxesInputs(storedPosition, 0, 2).x,
  );
  const [positionYInput, setPositionYInput] = useState<string>(
    () => deriveAxesInputs(storedPosition, 0, 2).y,
  );
  const [positionZInput, setPositionZInput] = useState<string>(
    () => deriveAxesInputs(storedPosition, 0, 2).z,
  );
  const [rotationPitchInput, setRotationPitchInput] = useState<string>(
    () => deriveAxesInputs(storedRotation, 0, 2).x,
  );
  const [rotationYawInput, setRotationYawInput] = useState<string>(
    () => deriveAxesInputs(storedRotation, 0, 2).y,
  );
  const [rotationRollInput, setRotationRollInput] = useState<string>(
    () => deriveAxesInputs(storedRotation, 0, 2).z,
  );
  const [scaleXInput, setScaleXInput] = useState<string>(
    () => deriveAxesInputs(storedScale, SCALE_DEFAULT, 2).x,
  );
  const [scaleYInput, setScaleYInput] = useState<string>(
    () => deriveAxesInputs(storedScale, SCALE_DEFAULT, 2).y,
  );
  const [scaleZInput, setScaleZInput] = useState<string>(
    () => deriveAxesInputs(storedScale, SCALE_DEFAULT, 2).z,
  );

  const activeMediaUrl =
    effectiveUrl && (is3D || storedKind === mediaKind) ? effectiveUrl : '';

  useEffect(() => {
    setName(hotspot?.name ?? '');
  }, [hotspotId, hotspot?.name]);

  useEffect(() => {
    setMediaKind(isVideoUrl(effectiveUrl) ? 'VIDEO' : 'IMAGE');
  }, [hotspotId, effectiveUrl]);

  useEffect(() => {
    if (!activeMediaUrl) {
      setLightboxOpen(false);
    }
  }, [activeMediaUrl]);

  /**
   * Ends any in-progress camera-view session when the selected hotspot
   * changes or the inspector unmounts. UE holds the camera on the hotspot
   * indefinitely otherwise — a stray FINISH is not the risk here, an
   * abandoned view is.
   */
  useEffect(() => {
    return () => {
      if (isViewingCameraRef.current) {
        endHotspotCameraViewInUnreal(sendMessageToUnreal, hotspotId);
        isViewingCameraRef.current = false;
      }
      setIsViewingCamera(false);
    };
  }, [hotspotId, sendMessageToUnreal]);

  /**
   * Resets the Location/Rotation/Scale editors to reflect the currently
   * selected hotspot.
   *
   * Only re-seeds when the *selected hotspot* changes — never on every
   * `storedPosition`/`storedRotation`/`storedScale` reference identity, which
   * flips on any WS-driven `setBrandingHotspots` refresh even when the
   * underlying value is byte-for-byte equal. A stored vec3 would arrive as a
   * fresh array on each refresh and clobber the user's uncommitted keystrokes.
   * A settled gizmo drag is not "any refresh" though — see the
   * `hotspotLiveTransform` effect below, which does intentionally overwrite.
   */
  useEffect(() => {
    const position = deriveAxesInputs(storedPosition, 0, 2);
    const rotation = deriveAxesInputs(storedRotation, 0, 2);
    const scale = deriveAxesInputs(storedScale, SCALE_DEFAULT, 2);
    setPositionXInput(position.x);
    setPositionYInput(position.y);
    setPositionZInput(position.z);
    setRotationPitchInput(rotation.x);
    setRotationYawInput(rotation.y);
    setRotationRollInput(rotation.z);
    setScaleXInput(scale.x);
    setScaleYInput(scale.y);
    setScaleZInput(scale.z);
    // storedPosition/storedRotation/storedScale are intentionally excluded — see JSDoc above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotspotId]);

  /**
   * Live-syncs the Location/Rotation/Scale inputs from a settled in-world
   * gizmo drag (`FINISH_HOTSPOT_PLACE_2D`/`_3D`, republished on the shared
   * `hotspotLiveTransform` context field). Unlike the effect above, this one
   * deliberately overwrites: a drag the admin just performed in-world is the
   * new authoritative transform, not background noise to protect keystrokes
   * from.
   */
  useEffect(() => {
    if (
      !hotspotLiveTransform ||
      hotspotLiveTransform.brandingHotspotId !== hotspotId
    ) {
      return;
    }
    if (hotspotLiveTransform.position) {
      const position = deriveAxesInputs(hotspotLiveTransform.position, 0, 2);
      setPositionXInput(position.x);
      setPositionYInput(position.y);
      setPositionZInput(position.z);
    }
    if (hotspotLiveTransform.rotation) {
      const rotation = deriveAxesInputs(hotspotLiveTransform.rotation, 0, 2);
      setRotationPitchInput(rotation.x);
      setRotationYawInput(rotation.y);
      setRotationRollInput(rotation.z);
    }
    if (hotspotLiveTransform.scale !== undefined) {
      const scale = deriveAxesInputs(
        hotspotLiveTransform.scale,
        SCALE_DEFAULT,
        2,
      );
      setScaleXInput(scale.x);
      setScaleYInput(scale.y);
      setScaleZInput(scale.z);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotspotLiveTransform, hotspotId]);

  /**
   * Runs the transform-gizmo edit session for as long as this placed hotspot
   * is selected: `START_HOTSPOT_EDIT` on mount, `END_HOTSPOT_EDIT` on
   * deselect/unmount. UE never ends this on its own, and re-selecting a
   * different hotspot remounts this component (`key={hotspotId}` in
   * `Inspector.tsx`), so the end-then-start pair falls out of the normal
   * mount/unmount cycle without extra bookkeeping.
   */
  useEffect(() => {
    if (!isPlacedInWorld) {
      return undefined;
    }
    startHotspotEditInUnreal(sendMessageToUnreal, hotspotId);
    return () => {
      endHotspotEditInUnreal(sendMessageToUnreal, hotspotId);
    };
  }, [hotspotId, isPlacedInWorld, sendMessageToUnreal]);

  const refresh = useCallback(async () => {
    if (!currentBrandingId) {
      return;
    }
    const refreshed = await BrandingsService.getBrandingHotspots(
      currentBrandingId,
      { limit: 200 },
    );
    setBrandingHotspots(refreshed.hotspots ?? []);
  }, [currentBrandingId, setBrandingHotspots]);

  const persist = useCallback(
    async (
      patch: IUpdateBrandingHotspotData,
    ): Promise<IBrandingHotspotApi | null> => {
      if (!currentBrandingId || !hotspotId) {
        return null;
      }
      setAutosaveStatus('saving');
      try {
        const updated = await BrandingsService.patchBrandingHotspot(
          currentBrandingId,
          hotspotId,
          patch,
        );
        await refresh();
        setAutosaveStatus('saved');
        return updated;
      } catch (saveError) {
        console.error(
          `Failed to update hotspot: ${(saveError as Error)?.message}`,
          { brandingId: currentBrandingId, brandingHotspotId: hotspotId },
        );
        setAutosaveStatus('error');
        return null;
      } finally {
        sendMessageToUnreal({ action: 'SPOTS_UPDATED', spots: [] });
      }
    },
    [
      currentBrandingId,
      hotspotId,
      refresh,
      sendMessageToUnreal,
      setAutosaveStatus,
    ],
  );

  const handleNameBlur = useCallback(() => {
    if (name !== (hotspot?.name ?? '')) {
      persist({ name });
    }
  }, [name, hotspot?.name, persist]);

  const handleToggleActive = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      persist({ isActive: checked });
    },
    [persist],
  );

  const handleToggleInteractive = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      persist({ isInteractive: checked });
    },
    [persist],
  );

  /**
   * Toggles the in-world camera-view session for this 3D hotspot, sending
   * `START_VIEW_HOTSPOT`/`END_VIEW_HOTSPOT`. Purely a live UE session — never
   * persisted, no PATCH, no `SPOTS_UPDATED` nudge.
   */
  const handleToggleCameraView = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.currentTarget.blur();
      if (isViewingCameraRef.current) {
        endHotspotCameraViewInUnreal(sendMessageToUnreal, hotspotId);
        isViewingCameraRef.current = false;
        setIsViewingCamera(false);
        return;
      }
      startHotspotCameraViewInUnreal(sendMessageToUnreal, hotspotId);
      isViewingCameraRef.current = true;
      setIsViewingCamera(true);
    },
    [hotspotId, sendMessageToUnreal],
  );

  /**
   * Handles blur on any Location/Rotation/Scale axis input. Reverts just the
   * blurred field when its own value is invalid; otherwise parses all nine
   * axis inputs and, once every one is valid, PATCHes
   * `{ position, rotation, scale }` together — but only if at least one
   * differs from what is stored.
   *
   * Full-transform batching (deliberate, and broader than the old per-field
   * batch-commit): UE currently drops a hotspot-update payload unless
   * position and rotation both arrive as complete 3-element arrays, so every
   * commit carries all three groups regardless of which single axis changed.
   * Uncommitted keystrokes on other fields ride along the same way the old
   * per-scale-axis batching did; a reverted axis re-enters the PATCH at its
   * stored value.
   *
   * @param field - Which transform group the blurred axis belongs to.
   * @param axis - Which axis input fired the blur.
   */
  const handleTransformAxisBlur = useCallback(
    (field: TransformField, axis: Axis) => {
      const inputs: Record<TransformField, Record<Axis, string>> = {
        position: {
          x: positionXInput,
          y: positionYInput,
          z: positionZInput,
        },
        rotation: {
          x: rotationPitchInput,
          y: rotationYawInput,
          z: rotationRollInput,
        },
        scale: { x: scaleXInput, y: scaleYInput, z: scaleZInput },
      };
      const setters: Record<
        TransformField,
        Record<Axis, (value: string) => void>
      > = {
        position: {
          x: setPositionXInput,
          y: setPositionYInput,
          z: setPositionZInput,
        },
        rotation: {
          x: setRotationPitchInput,
          y: setRotationYawInput,
          z: setRotationRollInput,
        },
        scale: { x: setScaleXInput, y: setScaleYInput, z: setScaleZInput },
      };
      const storedByField: Record<
        TransformField,
        number | number[] | undefined
      > = {
        position: storedPosition,
        rotation: storedRotation,
        scale: storedScale,
      };
      const fallbackByField: Record<TransformField, number> = {
        position: 0,
        rotation: 0,
        scale: SCALE_DEFAULT,
      };
      // All three groups display fixed to 2 decimals.
      const decimalsByField: Record<TransformField, number | undefined> = {
        position: 2,
        rotation: 2,
        scale: 2,
      };

      const fields: TransformField[] = ['position', 'rotation', 'scale'];
      const parsed = fields.reduce((acc, f) => {
        acc[f] = {
          x: parseFieldInput(f, inputs[f].x),
          y: parseFieldInput(f, inputs[f].y),
          z: parseFieldInput(f, inputs[f].z),
        };
        return acc;
      }, {} as Record<TransformField, Record<Axis, number | null>>);

      if (parsed[field][axis] === null) {
        // Invalid input on the axis that just blurred — revert only that field.
        const reverted = deriveAxesInputs(
          storedByField[field],
          fallbackByField[field],
          decimalsByField[field],
        );
        setters[field][axis](reverted[axis]);
        return;
      }

      const anyInvalid = fields.some(
        (f) =>
          parsed[f].x === null || parsed[f].y === null || parsed[f].z === null,
      );
      if (anyInvalid) {
        // Another field/axis is mid-edit and invalid — its own blur will handle it.
        return;
      }

      const nextPosition = [
        parsed.position.x,
        parsed.position.y,
        parsed.position.z,
      ] as [number, number, number];
      const nextRotation = [
        parsed.rotation.x,
        parsed.rotation.y,
        parsed.rotation.z,
      ] as [number, number, number];
      const nextScale = [parsed.scale.x, parsed.scale.y, parsed.scale.z] as [
        number,
        number,
        number,
      ];

      const unchanged =
        toTuple(storedPosition, 0).every((v, i) => v === nextPosition[i]) &&
        toTuple(storedRotation, 0).every((v, i) => v === nextRotation[i]) &&
        toTuple(storedScale, SCALE_DEFAULT).every((v, i) => v === nextScale[i]);
      if (unchanged) {
        return;
      }

      setPositionXInput(nextPosition[0].toFixed(2));
      setPositionYInput(nextPosition[1].toFixed(2));
      setPositionZInput(nextPosition[2].toFixed(2));
      setRotationPitchInput(nextRotation[0].toFixed(2));
      setRotationYawInput(nextRotation[1].toFixed(2));
      setRotationRollInput(nextRotation[2].toFixed(2));
      setScaleXInput(nextScale[0].toFixed(2));
      setScaleYInput(nextScale[1].toFixed(2));
      setScaleZInput(nextScale[2].toFixed(2));

      persist({
        position: nextPosition,
        rotation: nextRotation,
        scale: nextScale,
      });
    },
    [
      positionXInput,
      positionYInput,
      positionZInput,
      rotationPitchInput,
      rotationYawInput,
      rotationRollInput,
      scaleXInput,
      scaleYInput,
      scaleZInput,
      storedPosition,
      storedRotation,
      storedScale,
      persist,
    ],
  );

  const validateUploadFile = useCallback(
    (file: File): string | null => {
      const lowerName = file.name.toLowerCase();
      if (is3D) {
        if (
          MODEL_FILENAME_RE.test(lowerName) ||
          file.type === MODEL_MIME_TYPE
        ) {
          return null;
        }
        return translate(
          'editorMode.hotspotUnsupportedModel',
          'Unsupported format. Please upload a .glb 3D model file.',
        );
      }
      if (mediaKind === 'VIDEO') {
        if (
          ALLOWED_VIDEO_MIME_TYPES.includes(file.type) ||
          VIDEO_FILENAME_RE.test(lowerName)
        ) {
          return null;
        }
        return translate(
          'editorMode.hotspotUnsupportedVideo',
          'Unsupported format. Please upload an MP4, WebM, or MOV video file.',
        );
      }
      if (
        ALLOWED_IMAGE_MIME_TYPES.includes(file.type) ||
        IMAGE_FILENAME_RE.test(lowerName)
      ) {
        return null;
      }
      return translate(
        'editorMode.hotspotUnsupportedImage',
        'Unsupported format. Please upload a PNG or JPG image file.',
      );
    },
    [is3D, mediaKind, translate],
  );

  const handleUploadFiles = useCallback(
    async (files: FileList | null) => {
      const file = files?.[0];
      if (!file || !currentBrandingId) {
        return;
      }
      const validationError = validateUploadFile(file);
      if (validationError) {
        showNotification({
          message: validationError,
          type: NOTIFICATION_TYPES.ERROR,
        });
        return;
      }
      setIsUploading(true);
      setUploadProgress(0);
      setAutosaveStatus('saving');
      try {
        let fileMd5: string | undefined;
        if (is3D) {
          fileMd5 = md5(await file.arrayBuffer());
        }
        const [uploaded] = await FilesService.uploadFilesWithProgress(
          [file],
          (progress: number) => setUploadProgress(progress),
        );
        if (uploaded?.url) {
          const updated = await persist({
            data: {
              url: uploaded.url,
              location: uploaded.location,
              ...(fileMd5 ? { md5: fileMd5 } : {}),
              webBrowserUrl: '',
            },
          });
          const relocatedUrl = updated?.data?.url;
          if (is3D && !isPlacedInWorld && relocatedUrl) {
            const relocatedMd5 = updated?.data?.md5 ?? fileMd5;
            setPendingHotspotPlacement({
              brandingHotspotId: hotspotId,
              isCreated: true,
            });
            startHotspotPlaceInUnreal(sendMessageToUnreal, '3D', {
              brandingHotspotId: hotspotId,
              ...(name || hotspot?.name ? { name: name || hotspot?.name } : {}),
              ...(currentRoom?.roomId ? { roomId: currentRoom.roomId } : {}),
              data: {
                url: relocatedUrl,
                ...(relocatedMd5 ? { md5: relocatedMd5 } : {}),
              },
            });
            showNotification({
              message: translate(
                'editorMode.hotspotPlacementStarted',
                'Model uploaded — now place the object in the world.',
              ),
              type: NOTIFICATION_TYPES.SUCCESS,
            });
          }
        }
      } catch (uploadError) {
        console.error(
          `Failed to upload hotspot media: ${(uploadError as Error)?.message}`,
          { brandingId: currentBrandingId, brandingHotspotId: hotspotId },
        );
        setAutosaveStatus('error');
      } finally {
        setIsUploading(false);
      }
    },
    [
      currentBrandingId,
      currentRoom,
      hotspot,
      hotspotId,
      is3D,
      isPlacedInWorld,
      name,
      persist,
      sendMessageToUnreal,
      setAutosaveStatus,
      setPendingHotspotPlacement,
      showNotification,
      translate,
      validateUploadFile,
    ],
  );

  const handleUploadClick = useCallback(() => {
    const input = fileInputRef.current;
    if (!input) {
      return;
    }
    input.value = '';
    input.click();
  }, []);

  const handleDeleteMedia = useCallback(() => {
    persist({ data: { url: '', webBrowserUrl: '' } });
  }, [persist]);

  const handleSaveHotspot = useCallback(() => {
    persist({
      name,
      isActive: hotspot?.isActive !== false,
      ...(is3D ? { isInteractive: hotspot?.isInteractive !== false } : {}),
    });
  }, [persist, name, hotspot?.isActive, hotspot?.isInteractive, is3D]);

  const handleDelete = useCallback(async () => {
    if (!currentBrandingId || !hotspotId) {
      return;
    }
    setAutosaveStatus('saving');
    try {
      await BrandingsService.deleteBrandingHotspot(
        currentBrandingId,
        hotspotId,
      );
      await refresh();
      sendMessageToUnreal({ action: 'SPOTS_UPDATED', spots: [] });
      setAutosaveStatus('saved');
      clearSelection();
    } catch (deleteError) {
      console.error(
        `Failed to delete hotspot: ${(deleteError as Error)?.message}`,
        { brandingId: currentBrandingId, brandingHotspotId: hotspotId },
      );
      setAutosaveStatus('error');
    }
  }, [
    currentBrandingId,
    hotspotId,
    refresh,
    sendMessageToUnreal,
    setAutosaveStatus,
    clearSelection,
  ]);

  const mediaFilename = getFilename(activeMediaUrl);
  const showImageThumb = !is3D && !!activeMediaUrl && mediaKind === 'IMAGE';
  const showVideoThumb = !is3D && !!activeMediaUrl && mediaKind === 'VIDEO';
  const canZoomMedia = !is3D && !!activeMediaUrl;

  return (
    <Box ref={rootRef}>
      <Box sx={styles.section}>
        <Typography sx={styles.fieldLabel}>
          {translate('editorMode.hotspotName', 'Name')}
        </Typography>
        <TextField
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleNameBlur}
          size="small"
          fullWidth
          sx={styles.textField}
        />
      </Box>

      <Box sx={styles.section}>
        <Typography sx={styles.fieldLabel}>
          {is3D
            ? translate('editorMode.hotspotModel', '3D model')
            : mediaKind === 'VIDEO'
            ? translate('editorMode.video', 'Video')
            : translate('editorMode.hotspotImage', 'Image')}
        </Typography>
        {!is3D && (
          <Box sx={[styles.segmented, { marginBottom: '8px' }]}>
            <Box
              sx={[
                styles.segmentedItem,
                ...(mediaKind === 'IMAGE' ? [styles.segmentedItemActive] : []),
              ]}
              onClick={() => setMediaKind('IMAGE')}
            >
              {translate('editorMode.image', 'Image')}
            </Box>
            <Box
              sx={[
                styles.segmentedItem,
                ...(mediaKind === 'VIDEO' ? [styles.segmentedItemActive] : []),
              ]}
              onClick={() => setMediaKind('VIDEO')}
            >
              {translate('editorMode.video', 'Video')}
            </Box>
          </Box>
        )}
        {is3D && !effectiveUrl && (
          <Box sx={uploadModelBannerStyles}>
            <InfoOutlinedIcon
              sx={{ fontSize: 16, flexShrink: 0, marginTop: '1px' }}
            />
            <Typography sx={{ fontSize: '12px', lineHeight: 1.45 }}>
              {translate(
                'editorMode.hotspotUploadModelFirst',
                "Upload a 3D model (.glb) first — after the upload you'll place the object in the world.",
              )}
            </Typography>
          </Box>
        )}
        <Box sx={styles.mediaCard}>
          <Box sx={styles.compactRow}>
            <Box
              sx={[
                styles.compactThumb,
                ...(!showImageThumb && !showVideoThumb
                  ? [styles.spotThumbPlaceholder]
                  : []),
                ...(canZoomMedia ? [{ cursor: 'zoom-in' }] : []),
              ]}
              style={
                showImageThumb
                  ? { backgroundImage: `url("${activeMediaUrl}")` }
                  : undefined
              }
              onClick={canZoomMedia ? () => setLightboxOpen(true) : undefined}
            >
              {showVideoThumb && (
                <Box
                  component="video"
                  src={`${activeMediaUrl}#t=0.1`}
                  muted
                  playsInline
                  preload="metadata"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    pointerEvents: 'none',
                  }}
                />
              )}
              {!showImageThumb &&
                !showVideoThumb &&
                (is3D ? (
                  <ViewInArOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />
                ) : mediaKind === 'VIDEO' ? (
                  <PlayArrowOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />
                ) : (
                  <ImageOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />
                ))}
              {canZoomMedia && (
                <Box sx={styles.thumbExpandBadge}>
                  <OpenInFullOutlinedIcon sx={{ fontSize: 10 }} />
                </Box>
              )}
            </Box>

            <Box sx={styles.compactInfo}>
              <Typography sx={styles.compactTitle}>
                {mediaFilename ||
                  translate('editorMode.noMedia', 'No media set yet')}
              </Typography>
              <Typography sx={styles.compactSubtitle}>
                {is3D
                  ? translate('editorMode.hotspotModelKind', '3D model (.glb)')
                  : mediaKind === 'VIDEO'
                  ? translate('editorMode.video', 'Video')
                  : translate('editorMode.image', 'Image')}
              </Typography>
            </Box>

            <Box sx={styles.compactActions}>
              <IconButton
                size="small"
                sx={styles.iconSquareButton}
                onClick={handleUploadClick}
                disabled={isUploading}
                aria-label={translate('editorMode.uploadMedia', 'Upload')}
              >
                <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
              {!!activeMediaUrl && (
                <IconButton
                  size="small"
                  sx={[styles.iconSquareButton, styles.iconDangerButton]}
                  onClick={handleDeleteMedia}
                  disabled={isUploading}
                  aria-label={translate('editorMode.delete', 'Delete')}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          </Box>

          {isUploading && (
            <Box sx={styles.progressBlock}>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={styles.compactProgressBar}
              />
            </Box>
          )}
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          accept={
            is3D
              ? '.glb,model/gltf-binary'
              : mediaKind === 'VIDEO'
              ? 'video/mp4,video/webm,video/quicktime'
              : 'image/png,image/jpeg'
          }
          style={{ display: 'none' }}
          onChange={(e) => handleUploadFiles(e.target.files)}
        />
      </Box>

      {isPlacedInWorld && (
        <Box sx={styles.section}>
          <Typography sx={styles.fieldLabel}>
            {translate('editorMode.hotspotLocation', 'Location')}
          </Typography>
          <Box sx={{ display: 'flex', gap: '6px' }}>
            <TextField
              value={positionXInput}
              onChange={(e) => setPositionXInput(e.target.value)}
              onBlur={() => handleTransformAxisBlur('position', 'x')}
              size="small"
              fullWidth
              type="number"
              label={translate('editorMode.hotspotScaleX', 'X')}
              inputProps={{
                step: 0.01,
                'data-testid': 'hotspot-inspector-location-x-input',
              }}
              sx={styles.textField}
            />
            <TextField
              value={positionYInput}
              onChange={(e) => setPositionYInput(e.target.value)}
              onBlur={() => handleTransformAxisBlur('position', 'y')}
              size="small"
              fullWidth
              type="number"
              label={translate('editorMode.hotspotScaleY', 'Y')}
              inputProps={{
                step: 0.01,
                'data-testid': 'hotspot-inspector-location-y-input',
              }}
              sx={styles.textField}
            />
            <TextField
              value={positionZInput}
              onChange={(e) => setPositionZInput(e.target.value)}
              onBlur={() => handleTransformAxisBlur('position', 'z')}
              size="small"
              fullWidth
              type="number"
              label={translate('editorMode.hotspotScaleZ', 'Z')}
              inputProps={{
                step: 0.01,
                'data-testid': 'hotspot-inspector-location-z-input',
              }}
              sx={styles.textField}
            />
          </Box>
        </Box>
      )}

      {isPlacedInWorld && (
        <Box sx={styles.section}>
          <Typography sx={styles.fieldLabel}>
            {translate('editorMode.hotspotRotation', 'Rotation')}
          </Typography>
          <Box sx={{ display: 'flex', gap: '6px' }}>
            <TextField
              value={rotationPitchInput}
              onChange={(e) => setRotationPitchInput(e.target.value)}
              onBlur={() => handleTransformAxisBlur('rotation', 'x')}
              size="small"
              fullWidth
              type="number"
              label={translate('editorMode.hotspotRotationPitch', 'Pitch')}
              inputProps={{
                step: 0.01,
                'data-testid': 'hotspot-inspector-rotation-pitch-input',
              }}
              sx={styles.textField}
            />
            <TextField
              value={rotationYawInput}
              onChange={(e) => setRotationYawInput(e.target.value)}
              onBlur={() => handleTransformAxisBlur('rotation', 'y')}
              size="small"
              fullWidth
              type="number"
              label={translate('editorMode.hotspotRotationYaw', 'Yaw')}
              inputProps={{
                step: 0.01,
                'data-testid': 'hotspot-inspector-rotation-yaw-input',
              }}
              sx={styles.textField}
            />
            <TextField
              value={rotationRollInput}
              onChange={(e) => setRotationRollInput(e.target.value)}
              onBlur={() => handleTransformAxisBlur('rotation', 'z')}
              size="small"
              fullWidth
              type="number"
              label={translate('editorMode.hotspotRotationRoll', 'Roll')}
              inputProps={{
                step: 0.01,
                'data-testid': 'hotspot-inspector-rotation-roll-input',
              }}
              sx={styles.textField}
            />
          </Box>
        </Box>
      )}

      {isPlacedInWorld && (
        <Box sx={styles.section}>
          <Typography sx={styles.fieldLabel}>
            {translate('editorMode.hotspotScale', 'Scale')}
          </Typography>
          <Box sx={{ display: 'flex', gap: '6px' }}>
            <TextField
              value={scaleXInput}
              onChange={(e) => setScaleXInput(e.target.value)}
              onBlur={() => handleTransformAxisBlur('scale', 'x')}
              size="small"
              fullWidth
              type="number"
              label={translate('editorMode.hotspotScaleX', 'X')}
              inputProps={{
                min: SCALE_MIN,
                max: SCALE_MAX,
                step: 0.01,
                'data-testid': 'hotspot-inspector-scale-x-input',
              }}
              sx={styles.textField}
            />
            <TextField
              value={scaleYInput}
              onChange={(e) => setScaleYInput(e.target.value)}
              onBlur={() => handleTransformAxisBlur('scale', 'y')}
              size="small"
              fullWidth
              type="number"
              label={translate('editorMode.hotspotScaleY', 'Y')}
              inputProps={{
                min: SCALE_MIN,
                max: SCALE_MAX,
                step: 0.01,
                'data-testid': 'hotspot-inspector-scale-y-input',
              }}
              sx={styles.textField}
            />
            <TextField
              value={scaleZInput}
              onChange={(e) => setScaleZInput(e.target.value)}
              onBlur={() => handleTransformAxisBlur('scale', 'z')}
              size="small"
              fullWidth
              type="number"
              label={translate('editorMode.hotspotScaleZ', 'Z')}
              inputProps={{
                min: SCALE_MIN,
                max: SCALE_MAX,
                step: 0.01,
                'data-testid': 'hotspot-inspector-scale-z-input',
              }}
              sx={styles.textField}
            />
          </Box>
        </Box>
      )}

      <Box sx={styles.section}>
        <Box sx={activeRowStyles}>
          <Typography
            sx={{ fontSize: '13px', fontWeight: 500, color: COLORS.WHITE }}
          >
            {translate('editorMode.hotspotActive', 'Active')}
          </Typography>
          <Switch
            size="small"
            checked={hotspot?.isActive !== false}
            onChange={handleToggleActive}
            data-testid="hotspot-inspector-active-switch"
          />
        </Box>
      </Box>

      {is3D && (
        <Box sx={styles.section}>
          <Box sx={activeRowStyles}>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 500, color: COLORS.WHITE }}
            >
              {translate(
                'editorMode.hotspotInteractive',
                'Interactive in world',
              )}
            </Typography>
            <Switch
              size="small"
              checked={hotspot?.isInteractive !== false}
              onChange={handleToggleInteractive}
              inputProps={{
                'aria-label': translate(
                  'editorMode.hotspotInteractive',
                  'Interactive in world',
                ),
              }}
              data-testid="hotspot-inspector-interactive-switch"
            />
          </Box>
          <Typography sx={toggleHintStyles}>
            {translate(
              'editorMode.hotspotInteractiveHint',
              'Off — the object becomes fully static: it cannot be clicked and is not highlighted on hover.',
            )}
          </Typography>
        </Box>
      )}

      {is3D && isPlacedInWorld && (
        <Box sx={styles.section}>
          <Button
            fullWidth
            variant={isViewingCamera ? 'contained' : 'outlined'}
            startIcon={
              isViewingCamera ? (
                <VisibilityOffOutlinedIcon />
              ) : (
                <CameraAltOutlinedIcon />
              )
            }
            onClick={handleToggleCameraView}
            sx={
              isViewingCamera
                ? viewCameraButtonActiveStyles
                : viewCameraButtonStyles
            }
            data-testid="hotspot-inspector-view-camera-button"
          >
            {isViewingCamera
              ? translate('editorMode.hotspotExitCameraView', 'Hide path')
              : translate('editorMode.hotspotViewInWorld', 'Show path')}
          </Button>
          {isViewingCamera && (
            <Typography sx={toggleHintStyles}>
              {translate(
                'editorMode.hotspotCameraViewHint',
                "Your camera is locked on this hotspot's view. Click Exit camera view to return to normal.",
              )}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={styles.section}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={handleSaveHotspot}
          sx={saveButtonStyles}
        >
          {translate('editorMode.saveHotspot', 'Save hotspot')}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<DeleteOutlineIcon />}
          onClick={handleDelete}
          sx={deleteButtonStyles}
        >
          {translate('editorMode.deleteHotspot', 'Delete hotspot')}
        </Button>
      </Box>

      <Modal
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        sx={styles.lightboxBackdrop}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            outline: 'none',
          }}
          onClick={() => setLightboxOpen(false)}
        >
          {activeMediaUrl && mediaKind === 'VIDEO' ? (
            <Box
              component="video"
              src={activeMediaUrl}
              controls
              autoPlay
              sx={styles.lightboxImg}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
          ) : activeMediaUrl ? (
            <Box
              component="img"
              src={activeMediaUrl}
              alt={mediaFilename}
              sx={styles.lightboxImg}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
          ) : null}
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={styles.lightboxClose}
            size="small"
            aria-label={translate('editorMode.close')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Modal>
    </Box>
  );
};

export default HotspotInspector;
